// Filtering and grouping of the comments log. Pure, tested without a browser.
// Mirrors the old Comments page (DP_Research_Hub.gs buildCommentsView_ 7838).
import { GRANT_TYPE, hubDate, type CommentItem, type CommentMember, type CommentNode, type CommentsBundle } from '@pathonline/shared';
import { roleRank } from '../gantt/model.ts';

export type ReadState = 'new' | 'read' | 'all';
export type Order = 'timeline' | 'bytask';

export interface CommentFilters {
  scope: 'my' | 'all';
  read: ReadState;
  order: Order;
  /** Type groups switched OFF (empty = all shown). */
  hiddenTypes: string[];
  /** Task statuses shown; empty = all. */
  statuses: string[];
  programme: string;
  author: string;
  statusChangesOnly: boolean;
}

export const DEFAULT_COMMENT_FILTERS: CommentFilters = {
  scope: 'my',
  read: 'new',
  order: 'timeline',
  hiddenTypes: [],
  statuses: ['ACTIVE'],
  programme: '',
  author: '',
  statusChangesOnly: false,
};

/** The "Show" checkboxes: label → node types. Anything else is always shown. */
export const TYPE_GROUPS: [string, string[]][] = [
  ['Topics', ['Topic']],
  ['PhD', ['PhD']],
  ['MSc', ['MS']],
  ['BSc', ['BSc']],
  ['Tasks', ['Task']],
  ['Initiatives', ['Initiative']],
];

export const STATUS_ORDER = ['ACTIVE', 'SEEKING', 'IDEA', 'PENDING', 'APPLIED', 'FOLLOW', 'PAUSED', 'DONE'];

/** New = by someone else, after the person's read mark on that task. */
export function isNew(b: CommentsBundle, c: CommentItem): boolean {
  return c.email !== b.me.email && c.at > (b.seen[c.nodeId] ?? '');
}

export function filterComments(
  b: CommentsBundle,
  f: CommentFilters,
  opts: { search?: string; onlyNode?: string } = {},
): CommentItem[] {
  const nodes = new Map(b.nodes.map((n) => [n.id, n]));
  const mine = new Set(b.mine);
  const scope = b.scoped || !b.me.person ? (b.scoped ? 'my' : 'all') : f.scope;
  const hidden = new Set(TYPE_GROUPS.filter(([g]) => f.hiddenTypes.includes(g)).flatMap(([, types]) => types));
  const q = (opts.search ?? '').trim().toLowerCase();

  return b.comments.filter((c) => {
    const n = nodes.get(c.nodeId);
    if (opts.onlyNode && c.nodeId !== opts.onlyNode) return false;
    if (scope === 'my' && !mine.has(c.nodeId)) return false;
    if (f.read === 'new' && !isNew(b, c)) return false;
    if (f.read === 'read' && isNew(b, c)) return false;
    if (n && hidden.has(n.type)) return false;
    if (n && f.statuses.length && STATUS_ORDER.includes(n.status) && !f.statuses.includes(n.status)) return false;
    if (f.programme && n?.projectId !== f.programme) return false;
    if (f.author && c.author !== f.author) return false;
    if (f.statusChangesOnly && !c.status) return false;
    if (q && ![c.text, c.author, n?.label ?? '', c.status].some((s) => s.toLowerCase().includes(q))) return false;
    return true;
  });
}

export interface TaskGroup {
  node: CommentNode | undefined;
  nodeId: string;
  comments: CommentItem[];
}

/** By task: groups ordered by their newest comment, then by label; newest first inside. */
export function groupByTask(b: CommentsBundle, comments: CommentItem[]): TaskGroup[] {
  const nodes = new Map(b.nodes.map((n) => [n.id, n]));
  const by = new Map<string, CommentItem[]>();
  for (const c of comments) by.set(c.nodeId, [...(by.get(c.nodeId) ?? []), c]);
  return [...by]
    .map(([nodeId, list]) => ({ nodeId, node: nodes.get(nodeId), comments: list }))
    .sort(
      (x, y) =>
        y.comments[0]!.at.localeCompare(x.comments[0]!.at) || (x.node?.label ?? x.nodeId).localeCompare(y.node?.label ?? y.nodeId),
    );
}

/** "just now", "5m ago", "3h ago", "2d ago", "3w ago", else the date in Vilnius (old `rel`). */
export function relTime(iso: string, now: Date = new Date()): string {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return iso;
  const s = (now.getTime() - t) / 1000;
  if (s < 90) return 'just now';
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  if (s < 86_400) return `${Math.round(s / 3600)}h ago`;
  if (s < 7 * 86_400) return `${Math.round(s / 86_400)}d ago`;
  if (s < 35 * 86_400) return `${Math.round(s / (7 * 86_400))}w ago`;
  return hubDate(new Date(t));
}

/**
 * May the caller change this task's status with a comment? A scoped editor never changes a grant.
 * `scoped` is the comments/Gantt SelfFilter; the server checks the write scope (writeScopeOf).
 * They differ only for a role whose SelfFilter sits on another row; there the server decides.
 */
export function canSetStatusOn(b: CommentsBundle, n: CommentNode | undefined): boolean {
  return b.canSetStatus && !(b.scoped && n?.type === GRANT_TYPE);
}

export function sortedTeam(n: CommentNode | undefined): CommentMember[] {
  return [...(n?.team ?? [])].sort((a, b) => roleRank(a.role) - roleRank(b.role) || a.person.localeCompare(b.person));
}

// ── the hover preview and the pinned window (old DP:8277-8425); their place: ../place.ts ──

/** Comments the hover preview shows (old PREVIEW_MAX). */
export const PREVIEW_MAX = 5;

/** The preview: a task's newest comments, and how many earlier ones are left out. The log is newest first. */
export function previewOf(b: CommentsBundle, nodeId: string): { shown: CommentItem[]; more: number } {
  const all = b.comments.filter((c) => c.nodeId === nodeId);
  return { shown: all.slice(0, PREVIEW_MAX), more: Math.max(0, all.length - PREVIEW_MAX) };
}

/** The pinned window is a bottom sheet here: nothing to drag (old isNarrow). Matches comments.css. */
export const NARROW_MAX = 780;
